// MediaPicker — modal for picking a media file referenced by a block.
//
// Three sources, in priority order:
//   1. Already-uploaded files from the article's media_assets — clicked
//      filename is returned immediately.
//   2. Local file upload — file is uploaded to PB via the parent's
//      onUploadFile, the resulting filename is returned.
//   3. (For embeds) — separate component VideoEmbedBlockEditor accepts
//      URL directly; not handled here.
//
// Renders inline thumbnails for images, file-name only for video.

import { useRef, useState } from "react";
import { fileUrl } from "../../lib/pb";
import { t } from "./theme";

export interface MediaPickerProps {
  articleId: string;
  mediaAssets: string[];
  accept: "image" | "video" | "any";
  onPick: (filename: string) => void;
  onClose: () => void;
  onUploadFile: (file: File) => Promise<string>;
}

const ACCEPT_MAP: Record<MediaPickerProps["accept"], string> = {
  image: "image/jpeg,image/png,image/webp,image/avif",
  video: "video/mp4,video/webm,video/quicktime",
  any: "",
};

function looksLikeImage(name: string): boolean {
  return /\.(jpe?g|png|webp|avif|gif)$/i.test(name);
}
function looksLikeVideo(name: string): boolean {
  return /\.(mp4|webm|mov|m4v)$/i.test(name);
}

export function MediaPicker({
  articleId,
  mediaAssets,
  accept,
  onPick,
  onClose,
  onUploadFile,
}: MediaPickerProps) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = mediaAssets.filter((f) => {
    if (accept === "image") return looksLikeImage(f);
    if (accept === "video") return looksLikeVideo(f);
    return true;
  });

  async function handleFile(file: File) {
    setUploading(true);
    setErr(null);
    try {
      const filename = await onUploadFile(file);
      onPick(filename);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        fontFamily: t.sans,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 720,
          maxHeight: "80vh",
          background: t.card,
          color: t.text,
          borderRadius: 10,
          border: `1px solid ${t.border}`,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            Select {accept === "image" ? "image" : accept === "video" ? "video" : "media"}
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: t.text3,
              fontSize: 18,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16, overflow: "auto", flex: 1 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT_MAP[accept]}
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              style={{
                padding: "8px 14px",
                background: t.accent,
                border: `1px solid ${t.accent}`,
                color: "#1c1c1e",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {uploading ? "Uploading…" : "Upload new"}
            </button>
            {err && (
              <span style={{ fontSize: 11, color: t.danger, alignSelf: "center" }}>
                {err}
              </span>
            )}
          </div>

          {filtered.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: t.text3,
                fontSize: 13,
              }}
            >
              No {accept === "any" ? "media" : accept + "s"} uploaded yet on this article.
              Use “Upload new” above.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  accept === "video" ? "1fr" : "repeat(4, 1fr)",
                gap: 8,
              }}
            >
              {filtered.map((f) => {
                const isImage = looksLikeImage(f);
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => onPick(f)}
                    style={{
                      padding: 6,
                      background: t.bg,
                      border: `1px solid ${t.border}`,
                      borderRadius: 8,
                      cursor: "pointer",
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.borderColor =
                        t.accentStrong)
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLButtonElement).style.borderColor =
                        t.border)
                    }
                  >
                    {isImage ? (
                      <img
                        src={fileUrl("articles", articleId, f, "240x160")}
                        alt=""
                        style={{
                          width: "100%",
                          aspectRatio: "3 / 2",
                          objectFit: "cover",
                          borderRadius: 4,
                          background: t.cardRaised,
                          display: "block",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: "12px 10px",
                          fontSize: 13,
                          color: t.text2,
                          background: t.card,
                          borderRadius: 4,
                        }}
                      >
                        {looksLikeVideo(f) ? "▶ " : ""}
                        {f}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: 11,
                        color: t.text3,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                      title={f}
                    >
                      {f}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
